import {Button} from '../';
import {Link as MuiLink, Stack, Typography} from '@mui/material';
import styles from './DeviceLinkBox.module.css';
import { QrCodeIcon, SmartIdIcon } from '../../assets/images';
import * as Config from 'Config';
import i18n, {t} from 'i18next';
import {ReactNode, forwardRef } from 'react';

interface DeviceLinkBoxProps {
    isActive: boolean;
    isMobile: boolean;
    onStart: () => void;
    onActivate: () => void;
    notice?: ReactNode;
}

const SID_LINK_FALLBACK_URL = Config.SID_DEVICE_LINK_URL?.EN;

export const DeviceLinkBox = forwardRef<HTMLDivElement, DeviceLinkBoxProps>(
    ({isActive, isMobile, onStart, onActivate, notice}: DeviceLinkBoxProps, ref) => {
        const isDesktop = !isMobile;

        const currentLanguageCode = i18n.resolvedLanguage?.toUpperCase() ?? '';
        const sidLinkUrl = Config.SID_DEVICE_LINK_URL?.[currentLanguageCode] || SID_LINK_FALLBACK_URL;

        return (
            <div
                ref={ref}
                tabIndex={0}
                className={`${styles.deviceLinkBox} ${isActive ? styles.active : styles.inactive}`}
                onClick={onActivate}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onActivate();
                    }
                }}
            >
                <Stack
                    spacing={2}
                    sx={{
                        alignItems: 'stretch',
                        width: '100%'
                    }}>
                    <Typography
                        variant="body1"
                        component="p"
                        sx={{
                            fontWeight: 500,
                            textAlign: 'center'
                        }}>
                        {isMobile
                            ? (
                                <>
                                    {t('form.sid.DeviceLink.MobileDescription')}{' '}
                                    <MuiLink
                                        href={sidLinkUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        underline="always"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        ({t(isMobile ? 'form.sid.DeviceLink.LinkTextMobile' : 'form.sid.DeviceLink.LinkText')})
                                    </MuiLink>
                                </>
                            )
                            : t('form.sid.DeviceLink.QrDescription')}
                    </Typography>

                    {!isMobile && (
                        <Typography variant="body1" className={styles.learnMoreWrapper} sx={{
                            textAlign: 'center'
                        }}>
                            <MuiLink
                                href={sidLinkUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                underline="always"
                                className={styles.learnMoreLink}
                                onClick={(e) => e.stopPropagation()}
                            >
                                {t('form.sid.DeviceLink.LinkText')}
                                <span className={styles.srOnly}>
                                {' — '}{t('form.sid.DeviceLink.MoreInfoContext')}
                            </span>
                            </MuiLink>
                        </Typography>
                    )}

                    {notice || null}

                    <Button
                        appearance="primary"
                        size="m"
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onStart();
                        }}
                        {...{'data-testid': 'deviceLinkBtn'}}
                    >
                    <span className={styles.buttonContent}>
                    {isDesktop ? (
                        <>
                            <QrCodeIcon className={styles.qrButtonIcon} style={{marginRight: '0.5rem'}}/>
                            {t('form.sid.DeviceLink.showQrBtn')}
                        </>
                    ) : (
                        <>
                            <SmartIdIcon className={styles.smartIdButtonIcon} style={{marginRight: '0.5rem'}}/>
                            {t('form.sid.DeviceLink.OpenAppBtn')}
                        </>
                    )}
                    </span>
                    </Button>
                </Stack>
            </div>
        );
    }
);

DeviceLinkBox.displayName = 'DeviceLinkBox';